import { toMillis } from './time';

const BULK_RESULT_STATUSES = new Set(['complete', 'partial', 'failed']);
const BULK_RESULT_BOUNDARY_WIDGET_TYPES = new Set([
  'order_bulk_criteria',
  'order_bulk_update',
  'confirmation',
  'patient_summary',
  'order_summary',
  'patient_match',
  'order_match'
]);

function parseWidgetData(widgetData) {
  if (widgetData == null) return null;
  if (typeof widgetData === 'object') return widgetData;
  if (typeof widgetData !== 'string') return null;
  let current = widgetData;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) return null;
    try {
      current = JSON.parse(trimmed);
    } catch (_error) {
      return null;
    }
  }
  return typeof current === 'object' && current !== null ? current : null;
}

function toFiniteNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function normalizeBulkResultOutcome(message) {
  const data = parseWidgetData(message?.widgetData) || {};
  const result = typeof data.result === 'object' && data.result !== null ? data.result : {};
  const summary = typeof data.summary === 'object' && data.summary !== null ? data.summary : {};

  const rawStatus = String(
    data.resultStatus
    || result.status
    || ''
  ).trim().toLowerCase();

  const appliedCount = toFiniteNumber(result.appliedCount ?? summary.applied);
  const failedCount = toFiniteNumber(result.failedCount ?? summary.failed);
  const rolledBackCount = toFiniteNumber(result.rolledBackCount ?? summary.rolledBack);
  const totalCount = toFiniteNumber(result.totalCount ?? summary.totalRequested);
  const summaryText = String(result.summary || data.summaryText || data.summary || '').trim();

  let status = rawStatus;
  if (!BULK_RESULT_STATUSES.has(status)) {
    const applied = appliedCount ?? 0;
    const failed = failedCount ?? 0;
    if (failed > 0 && applied > 0) {
      status = 'partial';
    } else if (failed > 0) {
      status = 'failed';
    } else {
      status = 'complete';
    }
  }

  return {
    status,
    summary: summaryText,
    appliedCount: appliedCount ?? 0,
    totalCount: totalCount,
    failedCount: failedCount ?? 0,
    rolledBackCount: rolledBackCount ?? 0,
    sourceMessageId: message?.id || ''
  };
}

/**
 * Build a status-aware follow-up map for bulk preview cards.
 *
 * Each `order_bulk_update` message ID maps to either:
 * - `null` (no terminal bulk result found for this preview), or
 * - `{ status, summary, appliedCount, totalCount, failedCount, rolledBackCount, sourceMessageId }`
 *   derived from the associated `order_bulk_result` message.
 *
 * @param {Array<{ id: string, role?: string, widgetType?: string|null, widgetData?: any }>} mainMessages
 * @returns {Map<string, (null|{status:string,summary:string,appliedCount:number,totalCount:number|null,failedCount:number,rolledBackCount:number,sourceMessageId:string})>}
 */
export function buildBulkFollowUpByMessageId(mainMessages) {
  const map = new Map();
  for (let i = 0; i < mainMessages.length; i += 1) {
    const current = mainMessages[i];
    if (current?.widgetType !== 'order_bulk_update') continue;

    let outcome = null;
    for (let j = i + 1; j < mainMessages.length; j += 1) {
      const candidate = mainMessages[j];
      if (!candidate) continue;

      if (candidate.role === 'user') {
        break;
      }

      if (candidate.widgetType === 'order_bulk_result') {
        outcome = normalizeBulkResultOutcome(candidate);
        break;
      }

      if (BULK_RESULT_BOUNDARY_WIDGET_TYPES.has(candidate.widgetType)) {
        break;
      }
    }

    // Timestamp-tie fallback: when preview/result share the same timestamp,
    // UUID tie-break ordering can place `order_bulk_result` immediately before
    // `order_bulk_update` in list order. In that case, scan backward for a
    // same-timestamp result until a boundary/user row is reached.
    if (!outcome) {
      const previewMillis = toMillis(current.createdAt || current.timestamp);
      if (previewMillis > 0) {
        for (let j = i - 1; j >= 0; j -= 1) {
          const candidate = mainMessages[j];
          if (!candidate) continue;

          if (candidate.role === 'user') {
            break;
          }

          if (candidate.widgetType === 'order_bulk_result') {
            const candidateMillis = toMillis(candidate.createdAt || candidate.timestamp);
            if (candidateMillis === previewMillis) {
              outcome = normalizeBulkResultOutcome(candidate);
            }
            break;
          }

          if (BULK_RESULT_BOUNDARY_WIDGET_TYPES.has(candidate.widgetType)) {
            break;
          }
        }
      }
    }
    map.set(current.id, outcome);
  }
  return map;
}

/**
 * For confirmation widgets: true when an assistant outcome widget exists that completes the
 * confirm flow (not plain interim text like "Got it..." which would flip Option B to Complete too early).
 *
 * Includes `status` (generic backend result card). When timestamps tie, UUID sort can place the
 * outcome row before the confirmation row; confirmation rows get a fallback scan for completion
 * widget types (not summary/match) at the same millisecond or later.
 *
 * @param {Array<{ id: string, role?: string, widgetType?: string|null, createdAt?: string, timestamp?: string }>} mainMessages
 * @returns {Map<string, boolean>}
 */
export function buildAssistantFollowUpByMessageId(mainMessages) {
  const bulkFollowUpByMessageId = buildBulkFollowUpByMessageId(mainMessages);
  const OUTCOME_WIDGET_TYPES = new Set([
    'patient_result',
    'patient_summary',
    'patient_match',
    'patient_duplicate',
    'order_result',
    'order_summary',
    'order_match',
    'order_list',
    'order_duplicate',
    'order_bulk_result',
    'status'
  ]);
  const COMPLETION_OUTCOME_TYPES = new Set([
    'patient_result',
    'order_result',
    'order_list',
    'patient_duplicate',
    'order_duplicate',
    'order_bulk_result',
    'status'
  ]);
  const CONFIRMATION_LIKE_TYPES = new Set(['confirmation', 'order_bulk_update']);
  const map = new Map();
  for (let i = 0; i < mainMessages.length; i += 1) {
    if (mainMessages[i].widgetType === 'order_bulk_update') {
      map.set(mainMessages[i].id, !!bulkFollowUpByMessageId.get(mainMessages[i].id));
      continue;
    }
    let hasFollowUp = false;
    for (let j = i + 1; j < mainMessages.length; j += 1) {
      const m = mainMessages[j];
      if (m.role !== 'assistant' && m.role !== 'system') continue;
      if (OUTCOME_WIDGET_TYPES.has(m.widgetType)) {
        hasFollowUp = true;
        break;
      }
    }
    if (!hasFollowUp && CONFIRMATION_LIKE_TYPES.has(mainMessages[i].widgetType)) {
      const confTime = toMillis(mainMessages[i].createdAt || mainMessages[i].timestamp);
      for (let j = 0; j < mainMessages.length; j += 1) {
        if (j === i) continue;
        const m = mainMessages[j];
        if (m.role !== 'assistant' && m.role !== 'system') continue;
        if (!COMPLETION_OUTCOME_TYPES.has(m.widgetType)) continue;
        const mTime = toMillis(m.createdAt || m.timestamp);
        if (mTime > confTime || mTime === confTime) {
          hasFollowUp = true;
          break;
        }
      }
    }
    map.set(mainMessages[i].id, hasFollowUp);
  }
  return map;
}

/**
 * Hide redundant progress widgets that appear after a confirmation card (Option B replaces them).
 *
 * @param {Array<{ id: string, widgetType?: string|null }>} mainMessages
 * @returns {Map<string, boolean>}
 */
export function buildSuppressProgressAfterConfirmByMessageId(mainMessages) {
  const map = new Map();
  for (let i = 0; i < mainMessages.length; i += 1) {
    const m = mainMessages[i];
    if (m.widgetType !== 'progress') {
      map.set(m.id, false);
      continue;
    }
    let suppress = false;
    for (let j = i - 1; j >= 0; j -= 1) {
      const w = mainMessages[j].widgetType;
      if (
        w === 'patient_summary'
        || w === 'order_summary'
        || w === 'patient_match'
        || w === 'order_match'
      ) {
        break;
      }
      if (w === 'confirmation') {
        suppress = true;
        break;
      }
    }
    map.set(m.id, suppress);
  }
  return map;
}

/**
 * Hide duplicate completion cards that are emitted immediately after a confirmation card.
 * Option B keeps the user on a single card that transitions Pending -> Approved -> Complete.
 *
 * @param {Array<{ id: string, role?: string, widgetType?: string|null }>} mainMessages
 * @returns {Map<string, boolean>}
 */
export function buildSuppressOutcomeAfterConfirmByMessageId(mainMessages) {
  const map = new Map();
  const SUPPRESSIBLE_OUTCOMES = new Set([
    'patient_result',
    'order_result',
    'order_list',
    'patient_duplicate',
    'order_duplicate',
    'order_bulk_result'
  ]);
  const CONFIRMATION_LIKE_TYPES = new Set(['confirmation', 'order_bulk_update']);
  const isSuppressibleStatusOutcome = (message) => {
    if (message?.widgetType !== 'status') return false;
    const raw = message.widgetData;
    const data = typeof raw === 'object' && raw !== null ? raw : null;
    const statusText = String(data?.status?.text || '').trim().toLowerCase();
    const titleValue = String(
      Array.isArray(data?.children)
        ? (data.children.find((child) => child?.type === 'Title')?.value || '')
        : ''
    ).trim().toLowerCase();
    if (statusText === 'action complete') return true;
    if (titleValue.includes('record updated')) return true;
    if (titleValue.includes('order updated')) return true;
    return false;
  };

  for (let i = 0; i < mainMessages.length; i += 1) {
    const m = mainMessages[i];
    if (!SUPPRESSIBLE_OUTCOMES.has(m.widgetType) && !isSuppressibleStatusOutcome(m)) {
      map.set(m.id, false);
      continue;
    }

    let suppress = false;
    for (let j = i - 1; j >= 0; j -= 1) {
      const prev = mainMessages[j];
      const prevRole = prev.role;
      const prevWidget = prev.widgetType;

      if (prevRole === 'user') {
        break;
      }

      if (CONFIRMATION_LIKE_TYPES.has(prevWidget)) {
        suppress = true;
        break;
      }

      if (
        prevWidget === 'patient_summary'
        || prevWidget === 'order_summary'
        || prevWidget === 'patient_match'
        || prevWidget === 'order_match'
        || prevWidget === 'order_bulk_criteria'
      ) {
        break;
      }
    }

    map.set(m.id, suppress);
  }

  return map;
}
